import { useMemo } from "react";
import { Avatar, Style } from "@dicebear/core";
import glyphsDefinition from "@dicebear/styles/glyphs.json";
import { cn } from "~/lib/utils";
import { AGENT_BRAND_COLORS, Agent, AgentScope } from "~/services/agents";

const glyphsStyle = new Style(glyphsDefinition);

/** The subset of `Agent` needed to resolve a seed and color. `Agent` and `PartialAgent`
 * both satisfy this structurally, so callers can pass either straight through. */
export type AgentAvatarInput = Pick<
  Agent,
  "id" | "name" | "display_name" | "scope" | "avatar_seed" | "avatar_color"
>;

/** SYSTEM agents seed off `display_name` instead of `id`, which is per-customer-instance
 * and would otherwise give the same system agent a different avatar on every instance. A
 * persisted `avatar_seed` always wins over either fallback. */
export function agentAvatarSeed(agent: Partial<AgentAvatarInput> | null | undefined): string {
  if (!agent) {
    return "";
  }
  const { id, name, display_name, scope, avatar_seed } = agent;
  if (avatar_seed) {
    return avatar_seed;
  }
  const fallback = scope === AgentScope.SYSTEM ? display_name || id || name : id || name;
  return fallback || "";
}

/** The glyph shape Dicebear resolves for a seed (one of a fixed set of variants), without
 * paying the cost of rendering the SVG. Used to dedupe visually-identical seeds in a sample
 * set, and to identify which shape the selected seed corresponds to. */
export function resolveShapeVariant(seed: string): string {
  const avatar = new Avatar(glyphsStyle, { seed });
  return String(avatar.toJSON().options.shapeVariant);
}

const SHAPE_DISCOVERY_MAX_ATTEMPTS = 5000;
// Stop once this many consecutive probes turn up no new variant — cheap insurance against a
// pathological hash distribution, not a real limit (35 variants are found within ~650 probes).
const SHAPE_DISCOVERY_STALL_LIMIT = 500;

let cachedShapeSeeds: string[] | null = null;

/** One stable seed per distinct Dicebear glyph shape, ordered by variant id. Discovered by
 * probing a deterministic seed sequence (not Math.random) until no new shape has turned up
 * for a while, then cached — so the shape picker always shows the same fixed, complete set
 * without hardcoding how many shapes the style actually has. */
export function allShapeSeeds(): string[] {
  if (cachedShapeSeeds) {
    return cachedShapeSeeds;
  }
  const seedByVariant = new Map<string, string>();
  let stall = 0;
  for (let i = 0; i < SHAPE_DISCOVERY_MAX_ATTEMPTS && stall < SHAPE_DISCOVERY_STALL_LIMIT; i++) {
    const seed = `shape-${i}`;
    const variant = resolveShapeVariant(seed);
    if (seedByVariant.has(variant)) {
      stall++;
    } else {
      seedByVariant.set(variant, seed);
      stall = 0;
    }
  }
  cachedShapeSeeds = [...seedByVariant.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, seed]) => seed);
  return cachedShapeSeeds;
}

export interface AgentAvatarResult {
  /** `data:image/svg+xml` URI for the rendered avatar. */
  dataUri: string;
  /** The glyph color Dicebear resolved for this seed. */
  color: string;
}

/** The glyph color Dicebear resolves for a seed, without paying the cost of rendering the
 * SVG. `colorOverride` pins a persisted custom color instead of letting Dicebear resolve one
 * from `AGENT_BRAND_COLORS`. Prefer this over `generateAgentAvatar` when only the color is
 * needed (e.g. `AgentCard`'s accent ring) — `generateAgentAvatar` always renders and
 * base64-encodes the full SVG via `toDataUri()`. */
export function resolveAgentAvatarColor(seed: string, colorOverride?: string | null): string {
  const avatar = new Avatar(glyphsStyle, {
    seed,
    glyphColor: colorOverride ? [colorOverride] : AGENT_BRAND_COLORS,
  });
  const { options } = avatar.toJSON();
  const resolved = options.glyphColor;
  const resolvedColor = Array.isArray(resolved) ? resolved[0] : resolved;
  // Dicebear lowercases hex colors internally; restore the canonical AGENT_BRAND_COLORS
  // casing so callers can compare with `===` instead of a case-insensitive check.
  return (
    colorOverride ??
    AGENT_BRAND_COLORS.find((brand) => brand.toLowerCase() === resolvedColor.toLowerCase()) ??
    resolvedColor
  );
}

/** Deterministically renders the same avatar {@link AgentCard} reads its accent color from.
 * `colorOverride` pins a persisted custom color instead of letting Dicebear resolve one from
 * `AGENT_BRAND_COLORS`. */
export function generateAgentAvatar(
  seed: string,
  colorOverride?: string | null
): AgentAvatarResult {
  const avatar = new Avatar(glyphsStyle, {
    seed,
    glyphColor: colorOverride ? [colorOverride] : AGENT_BRAND_COLORS,
  });
  return {
    dataUri: avatar.toDataUri(),
    color: resolveAgentAvatarColor(seed, colorOverride),
  };
}

interface AgentAvatarProps {
  agent: Partial<AgentAvatarInput> | null | undefined;
  /** Pixel size of the rendered square. */
  size?: number;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
  title?: string;
}

export const AgentAvatar = ({
  agent,
  size = 44,
  className,
  title,
  "aria-hidden": ariaHidden,
}: AgentAvatarProps) => {
  const seed = agentAvatarSeed(agent);
  const { dataUri } = useMemo(
    () => generateAgentAvatar(seed, agent?.avatar_color),
    [seed, agent?.avatar_color]
  );

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        className
      )}
      style={{ width: size, height: size }}
      aria-hidden={ariaHidden}
      title={title}
    >
      <img src={dataUri} alt="" width={size} height={size} />
    </span>
  );
};
